import React, { useCallback, useContext, useMemo } from 'react';
import rolesConstants from '@utils/rolesConstants';
import { useTranslation } from 'react-i18next';
import { Select, Form, Button, message, FormInstance } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { LorawanActionTypes } from '@utils/deviceActions';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';
import api from '@services/api';

interface TransferPayloadProps {
  devEui: string;
  newCustomerVirtualAccountId: string;
}

interface TransfersProps {
  devices: TransferPayloadProps[];
  isUpload?: boolean;
  form: FormInstance<any>;
}

const useTransfers = ({ devices, isUpload, form }: TransfersProps) => {
  const { t } = useTranslation(['lorawan', 'device-actions-modal']);
  const {
    theme,
    selectedApp,
    isMotBusiness,
    hasPermission,
    customer: { customerLogged, customersSelectOptions },
  } = useContext(SessionContext);
  const navigate = useNavigate();
  const customerId = Form.useWatch('customerId', form);
  const requestId = Form.useWatch('requestId', form);

  const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);

  const { data: requestsByCustomer } = useSwr(
    customerId
      ? `${
          hasNewLora
            ? '/lorawan/virtual-accounts'
            : '/service-proxy/lorawanVirtualAccounts'
        }?customerId=${customerId}`
      : null,
  );

  const requestsOptions = useMemo(() => {
    const requests = requestsByCustomer?.content;

    return Array.isArray(requests)
      ? requests?.map((request: any) => ({
          label: `${request.id} - ${request.name}`,
          value: request?.id,
        }))
      : [];
  }, [requestsByCustomer?.content]);

  const transfersCustomersOptions = useMemo(() => {
    const options = [
      {
        label: `(${t('stock')}) ${customerLogged?.name ?? ' '}`,
        value: 0,
      },
    ];

    if (customersSelectOptions) {
      options.push({
        label: t('customer'),
        // @ts-ignore
        options: customersSelectOptions ?? [
          {
            label: (
              <div style={{ textAlign: 'center' }}>
                <LoadingOutlined />
              </div>
            ),
          },
        ],
      });
    }

    return options;
  }, [t, customersSelectOptions, customerLogged]);

  const steps = useMemo(() => {
    const defaultSteps = [
      {
        title: t(isMotBusiness ? 'department' : 'customer'),
        name: 'customerId',
        itemMessage: undefined,
        showItemMessage: false,
        content: (
          <Select
            options={transfersCustomersOptions}
            loading={!transfersCustomersOptions}
            disabled={!transfersCustomersOptions}
            optionFilterProp="label"
            allowClear
            showSearch
            style={{ width: '100%' }}
          />
        ),
      },
    ];
    if (customerId && customerId !== 0) {
      const disableStep = requestsOptions?.length === 0;
      defaultSteps.push({
        title: t(isMotBusiness ? 'grouping' : 'request'),
        name: 'requestId',
        itemMessage: t(
          isMotBusiness
            ? 'departmentWithoutGroupings'
            : 'contractWithoutRequests',
        ),
        showItemMessage: disableStep,
        content: <Select options={requestsOptions} disabled={disableStep} />,
      });
    }
    return defaultSteps;
  }, [
    customerId,
    isMotBusiness,
    requestsOptions,
    t,
    transfersCustomersOptions,
  ]);

  const payloadToSend = useMemo(
    (): TransferPayloadProps[] =>
      isUpload
        ? devices
        : devices?.map(device => ({
            devEui: device?.devEui,
            newCustomerVirtualAccountId: customerId === 0 ? 0 : requestId,
          })),
    [isUpload, devices, customerId, requestId],
  );

  const handleSuccess = useCallback(() => {
    const navigatePath = `/${selectedApp}/lorawan/operational/transfers`;
    message.success(
      <>
        <span>{t('device-actions-modal:msgs.requested-action')}</span>
        <Button
          type="link"
          style={{ margin: '0 4px', padding: 0 }}
          onClick={() => navigate(navigatePath)}
        >
          {t('device-actions-modal:msgs.follow-in-the-transfers-queue')}
        </Button>
      </>,
      2,
    );
  }, [selectedApp, t, navigate]);

  const sendDeviceAction = useCallback(async () => {
    await api.patch(
      `${!hasNewLora ? '/service-proxy' : ''}/lorawan/transfer`,
      payloadToSend,
    );
    handleSuccess();
  }, [handleSuccess, hasNewLora, payloadToSend]);

  const uploadCustomInfo = useMemo(
    () => (
      <span style={{ color: theme?.colors?.text1 }}>
        {t('transferRequestInfo')}
      </span>
    ),
    [t, theme?.colors?.text1],
  );

  return {
    steps,
    actionKey: LorawanActionTypes.TRANSFER,
    title: t(
      `device-actions:labels.lorawan.${rolesConstants.LORAWAN_TRANSFERS}`,
    ),
    sendDeviceAction,
    uploadCustomInfo,
  };
};

export default useTransfers;
